/**
 * Dashboard Theme Tokens
 *
 * Mode-aware theme system with dark and light variants.
 * Two-level accent system:
 * - Accent Primary: For CTAs, active nav, hero emphasis, featured KPI
 * - Accent Muted: For data viz, secondary highlights, progress bars, badges
 */

import type { DashboardMode, FontSize } from "./DashboardThemeProvider";
import type { ColorPalette } from "@/lib/theme/color-palettes";
import { getColorPalette } from "@/lib/theme/color-palettes";
import type { DashboardAppearance } from "@/lib/theme/dashboard-appearance";

/**
 * Convert hex color to rgba string
 */
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// Font size multipliers
const FONT_SIZE_MULTIPLIERS: Record<FontSize, number> = {
  small: 0.875, // 14px base
  medium: 1,    // 16px base (default)
  large: 1.125, // 18px base
};

export const getDashboardTheme = (
  mode: DashboardMode,
  paletteId?: string,
  customPrimaryColor?: string | null,
  gradientEnabled: boolean = true,
  fontSize: FontSize = "medium",
  appearance: DashboardAppearance = "modern",
) => {
  const isClassic = appearance === "classic";
  const effectiveGradientEnabled = isClassic ? false : gradientEnabled;
  const isDark = mode === "dark";
  const palette: ColorPalette = paletteId
    ? getColorPalette(paletteId as any, customPrimaryColor || undefined)
    : getColorPalette("default");

  // Base colors without gradients
  const darkBgSolid = "#0B0D10";
  const lightBgSolid = isClassic ? "#E4E8EE" : "#F6F8FB";
  
  // Font size multiplier
  const fontSizeMultiplier = FONT_SIZE_MULTIPLIERS[fontSize];

  const modernTheme = {
    mode,
    appearance,
    fontSize,
    fontSizeMultiplier,
    gradientEnabled: effectiveGradientEnabled,

    // Background colors
    bg: {
      primary: effectiveGradientEnabled
        ? (isDark
          ? "linear-gradient(135deg, #0B0D10 0%, #0A0B0E 50%, #0B0D10 100%)"
          : "linear-gradient(135deg, #F6F8FB 0%, #FFFFFF 55%, #F3F5FA 100%)")
        : (isDark ? darkBgSolid : lightBgSolid),
      sidebar: isDark ? "rgba(20,20,20,0.35)" : "rgba(255,255,255,0.45)",
    },

    // Glass surfaces
    surfaceGlass: {
      card: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.72)",
      cardStrong: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.82)",
      sidebar: isDark ? "rgba(20,20,20,0.35)" : "rgba(255,255,255,0.45)",
    },

    // Borders
    borderGlass: {
      default: isDark ? "rgba(255,255,255,0.12)" : "rgba(11,13,16,0.10)",
      subtle: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,13,16,0.06)",
    },

    // Text colors
    text: {
      primary: isDark ? "rgba(255,255,255,0.92)" : "rgba(11,13,16,0.92)",
      secondary: isDark ? "rgba(255,255,255,0.68)" : "rgba(11,13,16,0.68)",
      tertiary: isDark ? "rgba(255,255,255,0.45)" : "rgba(11,13,16,0.45)",
      muted: isDark ? "rgba(255,255,255,0.28)" : "rgba(11,13,16,0.28)",
    },

    // Accent colors - Two-level system (uses selected color palette)
    accent: {
      // Primary: Use sparingly for CTAs, active nav, 1 featured KPI, critical emphasis
      primary: palette.primary,
      primaryBg: isDark ? palette.primaryBg.dark : palette.primaryBg.light,
      primaryBorder: isDark ? palette.primaryBorder.dark : palette.primaryBorder.light,

      // Muted: Use for data viz, progress bars, secondary badges, subtle indicators
      muted: palette.muted,
      mutedHex: palette.mutedHex,
      mutedBg: isDark ? palette.mutedBg.dark : palette.mutedBg.light,
      mutedBorder: isDark ? palette.mutedBorder.dark : palette.mutedBorder.light,
    },

    // Mesh gradients (for background effects)
    mesh: {
      hero: {
        // Stronger in hero region
        accent: isDark ? palette.meshAccent.dark : palette.meshAccent.light,
        neutral: isDark ? "rgba(100,100,120,0.03)" : "rgba(100,100,120,0.02)",
      },
      content: {
        // Softer in content area
        accent: isDark ? palette.meshAccent.dark : palette.meshAccent.light,
        neutral: isDark ? "rgba(100,100,120,0.015)" : "rgba(100,100,120,0.01)",
      },
    },

    // Grain overlay
    grain: {
      opacity: isDark ? 0.05 : 0.03,
      mixBlendMode: isDark ? "overlay" : ("multiply" as const),
    },

    // Grid overlay
    grid: {
      opacity: isDark ? 0.3 : 0.15,
      color: isDark ? "rgba(255,255,255,0.015)" : "rgba(11,13,16,0.08)",
    },

    // Shadows
    shadow: {
      card: isDark
        ? "0 0 0 rgba(255,255,255,0.10), 0 18px 45px rgba(0,0,0,0.55)"
        : "0 0 0 rgba(255,255,255,0.65), 0 18px 45px rgba(11,13,16,0.10)",
      cardFeatured: isDark
        ? `0 0 0 rgba(255,255,255,0.10), 0 0 20px ${hexToRgba(palette.primary, 0.1)}, 0 18px 45px rgba(0,0,0,0.55)`
        : `0 0 0 rgba(255,255,255,0.65), 0 0 0 1px ${hexToRgba(palette.primary, 0.22)}, 0 0 22px ${hexToRgba(palette.primary, 0.12)}, 0 18px 45px rgba(11,13,16,0.10)`,
    },

    // Focus ring (using palette primary color)
    focusRing: isDark
      ? `0 0 0 3px ${hexToRgba(palette.primary, 0.3)}, 0 0 0 1px ${hexToRgba(palette.primary, 0.25)}`
      : `0 0 0 3px ${hexToRgba(palette.primary, 0.22)}, 0 0 0 1px ${hexToRgba(palette.primary, 0.18)}`,

    // Scrollbar
    scrollbar: {
      track: isDark ? "transparent" : "rgba(11,13,16,0.02)",
      thumb: isDark ? "rgba(255,255,255,0.15)" : "rgba(11,13,16,0.20)",
      thumbHover: isDark ? "rgba(255,255,255,0.25)" : hexToRgba(palette.primary, 0.25),
    },
  } as const;

  if (!isClassic) {
    return modernTheme;
  }

  const classicShared = {
    ...modernTheme,
    mesh: {
      hero: {
        accent: "transparent",
        neutral: "transparent",
      },
      content: {
        accent: "transparent",
        neutral: "transparent",
      },
    },
    grain: {
      opacity: 0,
      mixBlendMode: "normal" as const,
    },
    grid: {
      opacity: 0,
      color: "transparent",
    },
  } as const;

  if (isDark) {
    return {
      ...classicShared,
      bg: {
        primary: "#1A202C",
        sidebar: "#171923",
      },
      surfaceGlass: {
        card: "#2D3748",
        cardStrong: "#2D3748",
        sidebar: "#171923",
      },
      borderGlass: {
        default: "#4A5568",
        subtle: "#3D4858",
      },
      text: {
        primary: "#F7FAFC",
        secondary: "#E2E8F0",
        tertiary: "#A0AEC0",
        muted: "#718096",
      },
      shadow: {
        card: "0 1px 2px rgba(0, 0, 0, 0.24)",
        cardFeatured: `0 1px 3px rgba(0, 0, 0, 0.32), 0 0 0 1px ${hexToRgba(palette.primary, 0.35)}`,
      },
      focusRing: `0 0 0 2px ${hexToRgba(palette.primary, 0.45)}`,
      scrollbar: {
        track: "#2D3748",
        thumb: "#4A5568",
        thumbHover: hexToRgba(palette.primary, 0.55),
      },
    } as const;
  }

  return {
    ...classicShared,
    bg: {
      primary: "#E4E8EE",
      sidebar: "#F7F8FA",
    },
    surfaceGlass: {
      card: "#FFFFFF",
      cardStrong: "#FFFFFF",
      sidebar: "#F7F8FA",
    },
    borderGlass: {
      default: "#B8C2CE",
      subtle: "#D2DAE4",
    },
    text: {
      primary: "#1A202C",
      secondary: "#4A5568",
      tertiary: "#718096",
      muted: "#A0AEC0",
    },
    shadow: {
      card: "0 1px 2px rgba(0, 0, 0, 0.06)",
      cardFeatured: `0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px ${hexToRgba(palette.primary, 0.28)}`,
    },
    focusRing: `0 0 0 2px ${hexToRgba(palette.primary, 0.35)}`,
    scrollbar: {
      track: "#EDF2F7",
      thumb: "#A0AEC0",
      thumbHover: hexToRgba(palette.primary, 0.45),
    },
  } as const;
};

// Export types for external use
export type { DashboardMode } from "./DashboardThemeProvider";

// Backward compatibility: export default dark theme (for gradual migration)
export const dashboardTheme = getDashboardTheme("dark");
